import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Toast from '../../molecules/Toast';
import stripeService from '../../../services/stripeService';
import { StyledBilling } from '../SignUp/SignUp.styles';
import { AuthContext } from '../../../Context/authContext';
import { useContextHook } from 'use-context-hook';
const CardForm = forwardRef(({ setLoading }, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const options = useMemo(
    () => ({
      showIcon: true,
      hidePostalCode: true,
    }),
    [],
  );

  // CardForm.jsx
  const processHandler = async transactionDetails => {
    setLoading(true);
    if (!stripe || !elements) {
      Toast({ type: 'error', message: 'Stripe is not fully loaded.' });
      setLoading(false);
      return { response: null, result: null, error: { message: 'Stripe not loaded' } };
    }

    try {
      const cardElement = elements.getElement(CardNumberElement);
      // 1) Create SetupIntent server-side
      const setupIntentRes = await stripeService.createSetupIntent();
      if (!setupIntentRes?.clientSecret) {
        Toast({ type: 'error', message: 'Failed to create SetupIntent' });
        throw new Error('Failed to create SetupIntent');
      }

      // 2) Confirm SetupIntent with Stripe.js to save the card
      const { setupIntent, error } = await stripe.confirmCardSetup(setupIntentRes.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: transactionDetails?.fullName,
            email: transactionDetails?.email,
            address: {
              line1: transactionDetails?.street_address,
              country: transactionDetails?.country,
            },
            phone: transactionDetails?.phone_number,
          },
        },
      });
      if (error) {
        Toast({ type: 'error', message: error.message || 'Card confirmation failed.' });
        return { response: null, result: null, error };
      }

      const response = await stripeService.startSubscription({
        paymentMethodId: setupIntent.payment_method,
        priceId: transactionDetails?.price_id,
        country: transactionDetails?.countryName,
      });



      if (response?.success) {
        // Toast({ type: 'success', message: 'Free trial started' });
        // trialEndsAt: response.data.trialEndsAt
        return { response: response, result: setupIntent, error: null };
      }

      Toast({ type: 'error', message: response?.data?.message || 'Failed to activate account' });
      return { response: null, result: setupIntent, error: { message: 'Account creation failed' } };
    } catch (err) {
      Toast({ type: 'error', message: err.message || 'An error occurred.' });
      return { response: null, result: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const saveCardOnly = async transactionDetails => {
    setLoading(true);
    if (!stripe || !elements) {
      Toast({ type: 'error', message: 'Stripe is not fully loaded.' });
      setLoading(false);
      return { response: null, result: null, error: { message: 'Stripe not loaded' } };
    }

    try {
      const cardElement = elements.getElement(CardNumberElement);
      // 1) Create SetupIntent server-side
      const setupIntentRes = await stripeService.createSetupIntent();
      if (!setupIntentRes?.clientSecret) {
        Toast({ type: 'error', message: 'Failed to create SetupIntent' });
        throw new Error('Failed to create SetupIntent');
      }

      // 2) Confirm SetupIntent with Stripe.js to save the card
      const { setupIntent, error } = await stripe.confirmCardSetup(setupIntentRes.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: transactionDetails?.fullName,
            email: transactionDetails?.email,
            address: {
              line1: transactionDetails?.street_address,
              country: transactionDetails?.country,
            },
            phone: transactionDetails?.phone_number,
          },
        },
      });

      if (error) {
        Toast({ type: 'error', message: error.message || 'Card confirmation failed.' });
        return { response: null, result: null, error };
      }

      // 3) Save card details to backend
      const response = await stripeService.saveCardDetails({
        paymentMethodId: setupIntent.payment_method,
        country: transactionDetails?.countryName,
        fullName: transactionDetails?.fullName,
        street_address: transactionDetails?.street_address,
        phone_number: transactionDetails?.phone_number,
      });

      if (response?.success) {
        return { response: response, result: setupIntent, error: null };
      }

      Toast({ type: 'error', message: response?.message || 'Failed to save card' });
      return { response: null, result: setupIntent, error: { message: 'Save card failed' } };
    } catch (err) {
      Toast({ type: 'error', message: err.message || 'An error occurred.' });
      return { response: null, result: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    processHandler,
    saveCardOnly, // expose new handler for saving card only
  }));
  return (
    <StyledBilling>
      <div className="stripe-input ">
        <CardNumberElement options={options} className="card-element" />
      </div>

      <div className="two-col expiry-col">
        <div className="stripe-input">
          <CardExpiryElement options={options} className="card-element" />
        </div>
        <div className="stripe-input">
          <CardCvcElement options={options} className="card-element" />
        </div>
      </div>
    </StyledBilling>
  );
});

CardForm.displayName = 'CardForm';

export default CardForm;
